import _ from 'lodash'
import { EntityState } from '@reduxjs/toolkit'
import { CommonState, Model } from '@/core/classes/Model'
import { Admin } from './admin.model'
import { Document } from './document.model'
import { IdentityJustificationTypeItem } from './identity-justification.model'

export type OrdoclicVidalLicense = {
  id: string
  secret: string
}

export type DesmosConfig = {
  apiUrl: string
  identityApiUrl: string
  finess?: string
  login?: string
  password?: string
}

export type AirtableConfig = {
  enabled?: boolean
  baseId?: string
  patientTableId?: string
}

export type AirtableConfigResponse = AirtableConfig & {
  hasApiKey: boolean
  inboundSyncEnabled?: boolean
  hasWebhookSecret?: boolean
  webhookId?: string
  webhookNotificationUrl?: string
  missingPublicWebhookUrl?: boolean
}

export type AirtableBaseListItem = {
  id: string
  name: string
  permissionLevel?: string
}

export type AirtableBasesDiscovery = {
  bases: AirtableBaseListItem[]
}

export type UpdateAirtableConfigDto = AirtableConfig & {
  apiKey?: string
}

export type AirtablePatientTableStatus = {
  exists: boolean
  configured: boolean
  tableId?: string
  tableName: string
  missingFieldNames: string[]
}

export type ExportMedConnectPatientsToAirtableResult = {
  patientTableId: string
  scanned: number
  created: number
  updated: number
  unchanged: number
  skippedInvalid: number
}

export type AirtableInboundSyncStatus = {
  enabled: boolean
  webhookConfigured: boolean
  webhookPath: string
  webhookMode?: 'airtable_api' | 'manual'
  webhookId?: string
  webhookNotificationUrl?: string
  webhookExpirationTime?: string
  lastWebhookNotificationAt?: string
  missingPublicWebhookUrl?: boolean
  webhookSecret?: string
  lastReconciledAt?: string
  recentFailureCount: number
  needsReviewCount: number
}

export type AirtableSetupVerificationStepStatus = 'pending' | 'success' | 'failed'

export type AirtableSetupVerificationStep = {
  key:
  | 'webhook_registered'
  | 'create_event_received'
  | 'patient_created'
  | 'update_event_received'
  | 'patient_updated'
  | 'delete_event_received'
  | 'test_data_cleaned_up'
  label: string
  status: AirtableSetupVerificationStepStatus
  sanitizedError?: string
}

export type AirtableSetupVerificationResult = {
  success: boolean
  marker: string
  steps: AirtableSetupVerificationStep[]
}

export enum StructureActivityCode {
  /** Etablissement public de santé */
  SA01 = 'SA01',
  /** Hôpital militaire du Service de santé des armées */
  SA02 = 'SA02',
  /** Etablissement privé PSPH */
  SA03 = 'SA03',
  /** Etablissement privé non PSPH */
  SA04 = 'SA04',
  /** Centre de santé */
  SA05 = 'SA05',
  /** Autre structure du Service de santé des armées */
  SA06 = 'SA06',
  /** Cabinet individuel */
  SA07 = 'SA07',
  /** Cabinet de groupe */
  SA08 = 'SA08',
  /** Exercice en société */
  SA09 = 'SA09',
  /** Transporteur sanitaire */
  SA10 = 'SA10',
  /** Entreprise d'intérim */
  SA11 = 'SA11',
  /** Etablissement de soins et prévention */
  SA12 = 'SA12',
  /** Prévention et soins en entreprise */
  SA13 = 'SA13',
  /** Etablissement scolaire ou universitaire */
  SA14 = 'SA14',
  /** PMI et Planification familiale */
  SA15 = 'SA15',
  /** Etablissement pour personnes handicapées */
  SA16 = 'SA16',
  /** Etablissement pour personnes âgées */
  SA17 = 'SA17',
  /** Etablissement aide à la famille */
  SA18 = 'SA18',
  /** Etablissement d'enseignement */
  SA19 = 'SA19',
  /** Etablissement pour la protection de l'enfance */
  SA20 = 'SA20',
  /** Etablissement d'hébergement et de réadaptation */
  SA21 = 'SA21',
  /** Recherche */
  SA22 = 'SA22',
  /** Assurance privée */
  SA23 = 'SA23',
  /** Organisme de sécurité sociale */
  SA24 = 'SA24',
  /** Laboratoire de biologie médicale */
  SA25 = 'SA25',
  /** Ministère ou Service déconcentré */
  SA26 = 'SA26',
  /** Collectivité territoriale */
  SA27 = 'SA27',
  /** Association ou organisme humanitaire */
  SA28 = 'SA28',
  /** Laboratoire d'analyses et de biologie médicale */
  SA29 = 'SA29',
  /** Autre établissement sanitaire */
  SA30 = 'SA30',
  /** Répartition et Distribution de médicaments et dispositifs médicaux */
  SA31 = 'SA31',
  /** Fabrication, Exploitation et Importation de médicaments et dispositifs médicaux */
  SA32 = 'SA32',
  /** Pharmacie d'officine */
  SA33 = 'SA33',
  /** Centre de dialyse */
  SA34 = 'SA34',
  /** Para-pharmacie */
  SA35 = 'SA35',
  /** Centre anti-cancer */
  SA36 = 'SA36',
  /** Centre de transfusion sanguine */
  SA37 = 'SA37',
  /** Pharmacie minière */
  SA38 = 'SA38',
  /** Pharmacie mutualiste */
  SA39 = 'SA39',
  /** Secteur privé, praticien hospitalier temps plein */
  SA40 = 'SA40',
  /** Autre établissement du domaine social ou médico-social */
  SA41 = 'SA41',
  /** Appareillage médical */
  SA42 = 'SA42',
  /** Secteur non défini */
  SA43 = 'SA43',
  /** Recrutement ou Gestion RH */
  SA44 = 'SA44',
  /** Communication, Marketing, Consulting, Média */
  SA45 = 'SA45',
  /** Fabrication, Exploitation, Importation, Répartition, Distribution médicaments et dispositifs médicaux */
  SA46 = 'SA46',
  /** Incendie et secours */
  SA47 = 'SA47',
  /** Entreprise industrielle et tertiaire hors industrie pharmaceutique */
  SA48 = 'SA48',
  /** Entité Outre-Mer */
  SA49 = 'SA49',
  /** Structure d'urgence hors établissement hospitalier */
  SA50 = 'SA50',
  /** Structure de coopération avec autorisation de soins */
  SA51 = 'SA51',
  /** Maison de santé ou Pôle de santé */
  SA52 = 'SA52',
  /** Siège administratif d'un établissement de santé */
  SA53 = 'SA53',
  /** Centre de dépistage et de prévention */
  SA54 = 'SA54',
  /** Laboratoire pharmaceutique préparant et délivrant des allergènes */
  SA55 = 'SA55',
  /** Propharmacie */
  SA56 = 'SA56',
  /** Structure de coopération avec mission de coordination de soins */
  SA57 = 'SA57',
  /** Entreprise de taxis */
  SA58 = 'SA58',
  /** Laboratoire de biologie médicale hors catégorie standard */
  SA59 = 'SA59',
  /** Centres médicaux SNCF */
  SA60 = 'SA60',
  /** Structure Dispensatrice à domicile d'Oxygène à usage médical */
  SA61 = 'SA61',
}

export type EstablishmentExtraInfo = {
  finess?: string
  address?: string
  zipCode?: string
  city?: string
  country?: string
  phone?: string
  structureActivity?: {
    code: StructureActivityCode
  }
}

export type Establishment = {
  _id: string
  contract?: string
  name: string
  active?: boolean
  admin?: Pick<Admin, '_id' | 'accountType' | 'userInfo'>
  plugins?: Plugin[]
  patientsPrivacy?: string
  logo?: {
    url: string
  }
  ordoclicEntityId?: string
  meetingBackgrounds?: Document[]
  ordoclicVidalLicense?: OrdoclicVidalLicense
  desmosConfig?: DesmosConfig
  airtableConfig?: AirtableConfig
  isJoinableByPatients?: boolean
  identityJustificationTypes?: IdentityJustificationTypeItem[]
  autoValidateIdentity?: boolean
  preferredLanguage?: string
} & EstablishmentExtraInfo

export type CreateEstablishmentDto = {
  name: string
  active?: boolean
  admin?: string
  preferredLanguage?: string
} & EstablishmentExtraInfo

export type UpdateEstablishmentDto = {
  name?: string
  logo?: string
  patientsPrivacy?: string
  ordoclicVidalLicense?: OrdoclicVidalLicense
  desmosConfig?: DesmosConfig
  isJoinableByPatients?: boolean
  identityJustificationTypes?: IdentityJustificationTypeItem[]
  autoValidateIdentity?: boolean
  preferredLanguage?: string | null
} & EstablishmentExtraInfo

export type Plugin = {
  _id?: string
  apiKey: string
  apiUrl: string
  pluginUrl: string
  status?: boolean
}

export type EstablishmentState = CommonState & {
  establishments: EntityState<Establishment, Establishment['_id']>
}

class EstablishmentModel extends Model {
  static instance: EstablishmentModel

  static getInstance() {
    if (!EstablishmentModel.instance) {
      EstablishmentModel.instance = new EstablishmentModel('establishment')
    }
    return EstablishmentModel.instance
  }

  getInitialState(): EstablishmentState {
    return _.merge({}, this.commonState, {
      establishments: {
        entities: {},
        ids: [],
      },
    })
  }

  getEstablishment = async (id: string | Establishment): Promise<Establishment> => {
    return await this.httpClient.request<Establishment>(`/etablissements/${id}`, 'GET')
  }
}

export default EstablishmentModel.getInstance()
